import React, { useEffect, useLayoutEffect, useCallback, useState, useRef } from 'react';

/** Uzaklaştırma en fazla %100 (daha küçük ölçek yok) */
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.15;
/** px — daha küçük sürükleme tıklama sayılır (il poligonu vb.) */
const PAN_DRAG_THRESHOLD = 6;
/** Etiket punto güncellemesi: zoom sırasında layout titremesini azaltır */
const LABEL_ZOOM_DEBOUNCE_MS = 48;

function clampZoom(v) {
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(v * 1000) / 1000));
}

function isDraggableLabelTarget(target) {
  return Boolean(target?.closest?.('.province-label--draggable'));
}

/**
 * Harita SVG’sini saran yakınlaştırma. Kaydırma çubuğu yok; tutup sürükle (Google Haritalar).
 * Tekerlek: imleç altındaki nokta sabit kalır (rAF ile birleştirilir).
 * @param {{ children: React.ReactNode, resetKey?: string, hideToolbar?: boolean, className?: string }} props
 */
export function MapZoomableFrame({ children, resetKey = '', hideToolbar = false, className = '' }) {
  const [scale, setScale] = useState(1);
  const scaleRef = useRef(1);
  const viewportRef = useRef(null);
  const scalerRef = useRef(null);
  const wheelRafRef = useRef(0);
  const wheelAccumFactorRef = useRef(1);
  const wheelVxRef = useRef(0);
  const wheelVyRef = useRef(0);

  scaleRef.current = scale;

  useEffect(() => {
    setScale(1);
    scaleRef.current = 1;
    const v = viewportRef.current;
    const s = scalerRef.current;
    if (s) {
      s.style.width = '';
    }
    if (v) {
      v.scrollLeft = 0;
      v.scrollTop = 0;
    }
  }, [resetKey]);

  const applyZoomAtContentPoint = useCallback((newScale, contentX, contentY) => {
    const v = viewportRef.current;
    const scaler = scalerRef.current;
    const old = scaleRef.current;
    const s2 = clampZoom(newScale);
    if (s2 === old || !v) return;
    const vx = contentX - v.scrollLeft;
    const vy = contentY - v.scrollTop;
    const z = s2 / old;
    if (scaler) {
      scaler.style.width = `${s2 * 100}%`;
    }
    v.scrollLeft = contentX * z - vx;
    v.scrollTop = contentY * z - vy;
    scaleRef.current = s2;
    setScale(s2);
  }, []);

  const zoomIn = useCallback(() => {
    const v = viewportRef.current;
    const old = scaleRef.current;
    if (!v) {
      setScale((s) => clampZoom(s + ZOOM_STEP));
      return;
    }
    const ax = v.scrollLeft + v.clientWidth / 2;
    const ay = v.scrollTop + v.clientHeight / 2;
    applyZoomAtContentPoint(old + ZOOM_STEP, ax, ay);
  }, [applyZoomAtContentPoint]);

  const zoomOut = useCallback(() => {
    const v = viewportRef.current;
    const old = scaleRef.current;
    if (!v) {
      setScale((s) => clampZoom(s - ZOOM_STEP));
      return;
    }
    const ax = v.scrollLeft + v.clientWidth / 2;
    const ay = v.scrollTop + v.clientHeight / 2;
    applyZoomAtContentPoint(old - ZOOM_STEP, ax, ay);
  }, [applyZoomAtContentPoint]);

  const reset = useCallback(() => {
    const el = viewportRef.current;
    const scaler = scalerRef.current;
    if (scaler) {
      scaler.style.width = '';
    }
    scaleRef.current = 1;
    setScale(1);
    if (el) {
      el.scrollLeft = 0;
      el.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return undefined;

    const flushWheel = () => {
      wheelRafRef.current = 0;
      const scaler = scalerRef.current;
      if (!v || !scaler) return;
      const factor = wheelAccumFactorRef.current;
      wheelAccumFactorRef.current = 1;
      if (factor === 1) return;
      const old = scaleRef.current;
      const s2 = clampZoom(old * factor);
      if (s2 === old) return;
      const z = s2 / old;
      const vx = wheelVxRef.current;
      const vy = wheelVyRef.current;
      const cx = v.scrollLeft + vx;
      const cy = v.scrollTop + vy;
      scaler.style.width = `${s2 * 100}%`;
      v.scrollLeft = cx * z - vx;
      v.scrollTop = cy * z - vy;
      scaleRef.current = s2;
      setScale(s2);
    };

    const onWheel = (e) => {
      if (e.cancelable) e.preventDefault();
      const rect = v.getBoundingClientRect();
      wheelVxRef.current = e.clientX - rect.left;
      wheelVyRef.current = e.clientY - rect.top;
      const factor = 1 - Math.sign(e.deltaY) * 0.12;
      wheelAccumFactorRef.current *= factor;
      if (!wheelRafRef.current) {
        wheelRafRef.current = requestAnimationFrame(flushWheel);
      }
    };

    v.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      v.removeEventListener('wheel', onWheel);
      if (wheelRafRef.current) {
        cancelAnimationFrame(wheelRafRef.current);
        wheelRafRef.current = 0;
      }
      wheelAccumFactorRef.current = 1;
    };
  }, [resetKey]);

  useEffect(() => {
    const v = viewportRef.current;
    if (!v) return undefined;

    let activeId = null;
    let startX = 0;
    let startY = 0;
    let startSl = 0;
    let startSt = 0;
    let panning = false;
    /** Pan başlamadan capture etme — il poligonu tıklaması bozulmasın */
    let captureActive = false;

    const blockNextClick = () => {
      const block = (ce) => {
        ce.preventDefault();
        ce.stopPropagation();
        ce.stopImmediatePropagation();
        document.removeEventListener('click', block, true);
      };
      document.addEventListener('click', block, true);
    };

    const onPointerDown = (e) => {
      if (e.button !== 0 && e.pointerType !== 'touch') return;
      if (isDraggableLabelTarget(e.target)) return;
      activeId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      startSl = v.scrollLeft;
      startSt = v.scrollTop;
      panning = false;
      captureActive = false;
    };

    const onPointerMove = (e) => {
      if (e.pointerId !== activeId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!panning) {
        if (Math.hypot(dx, dy) < PAN_DRAG_THRESHOLD) return;
        panning = true;
        v.classList.add('tech-map-zoom__viewport--dragging');
        if (!captureActive) {
          try {
            v.setPointerCapture(e.pointerId);
            captureActive = true;
          } catch {
            /* */
          }
        }
      }
      v.scrollLeft = startSl - dx;
      v.scrollTop = startSt - dy;
    };

    const endPan = (e) => {
      if (activeId === null) {
        v.classList.remove('tech-map-zoom__viewport--dragging');
        return;
      }
      if (e && e.pointerId !== activeId) return;
      if (captureActive) {
        try {
          v.releasePointerCapture(activeId);
        } catch {
          /* */
        }
        captureActive = false;
      }
      if (panning) {
        blockNextClick();
      }
      panning = false;
      activeId = null;
      v.classList.remove('tech-map-zoom__viewport--dragging');
    };

    v.addEventListener('pointerdown', onPointerDown);
    v.addEventListener('pointermove', onPointerMove);
    v.addEventListener('pointerup', endPan);
    v.addEventListener('pointercancel', endPan);

    return () => {
      endPan(null);
      v.removeEventListener('pointerdown', onPointerDown);
      v.removeEventListener('pointermove', onPointerMove);
      v.removeEventListener('pointerup', endPan);
      v.removeEventListener('pointercancel', endPan);
    };
  }, [resetKey]);

  useLayoutEffect(() => {
    const scaler = scalerRef.current;
    if (!scaler) return undefined;

    const applyLabelFontZoom = () => {
      const z = scaleRef.current;
      if (!z || z <= 0) return;
      scaler.querySelectorAll('text[data-map-label-fs-base]').forEach((el) => {
        const base = parseFloat(el.getAttribute('data-map-label-fs-base'), 10);
        if (!Number.isFinite(base) || base <= 0) return;
        const fs = Math.round((base / z) * 1000) / 1000;
        el.setAttribute('font-size', String(fs));
      });
    };

    let debounceTimer = null;
    const scheduleApply = () => {
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = null;
        applyLabelFontZoom();
      }, LABEL_ZOOM_DEBOUNCE_MS);
    };

    scheduleApply();

    const mo = new MutationObserver(() => {
      scheduleApply();
    });
    mo.observe(scaler, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-map-label-fs-base'] });
    return () => {
      mo.disconnect();
      if (debounceTimer != null) {
        clearTimeout(debounceTimer);
      }
      applyLabelFontZoom();
    };
  }, [scale, resetKey]);

  return (
    <div className={`tech-map-zoom ${className}`.trim()}>
      {!hideToolbar && (
        <div className="tech-map-zoom__toolbar" role="toolbar" aria-label="Harita ölçeği">
          <button
            type="button"
            className="tech-map-zoom__btn"
            onClick={zoomOut}
            disabled={scale <= ZOOM_MIN}
            aria-label="Uzaklaştır"
          >
            <i className="fas fa-minus" aria-hidden="true" />
          </button>
          <span className="tech-map-zoom__pct" aria-live="polite">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            className="tech-map-zoom__btn"
            onClick={zoomIn}
            disabled={scale >= ZOOM_MAX}
            aria-label="Yakınlaştır"
          >
            <i className="fas fa-plus" aria-hidden="true" />
          </button>
          <button type="button" className="tech-map-zoom__btn tech-map-zoom__btn--text" onClick={reset} aria-label="Ölçeği sıfırla">
            1:1
          </button>
        </div>
      )}
      <div ref={viewportRef} className="tech-map-zoom__viewport">
        <div ref={scalerRef} className="tech-map-zoom__scaler" style={{ width: `${scale * 100}%` }}>
          {children}
        </div>
      </div>
    </div>
  );
}
